import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import { FormEvent, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    updateNewJobData,
    useAddJobCategoryMutation,
    useGetJobCategoriesQuery,
} from "slice/jobs";
import { validations } from "utils/validations";
import { useEditJob } from "./useEditJob";

export const useJobPost = () => {
    const [agency, setAgency] = useState(-1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [categoryName, setCategoryName] = useState("");
    const [categoryNameError, setCategoryNameError] = useState("");

    const { id: editJobId } = useParams();

    const { id } = useAppSelector((state) => state.user);
    const { newJobData } = useAppSelector((state) => state.jobs);
    const dispatch = useDispatch(),
        navigateTo = useNavigate();

    useEditJob();

    const { data, isFetching, refetch } = useGetJobCategoriesQuery(id ?? "");
    const [
        addCategory,
        {
            data: jobCategoryData,
            isLoading: isSubmittingForm,
            isError,
            isSuccess,
        },
    ] = useAddJobCategoryMutation();

    const handleAgencyChange = (agency: number) => setAgency(agency);

    const toggleModal = (shouldResetCategoryName?: boolean) => {
        setIsModalOpen((prev) => !prev);

        if (shouldResetCategoryName) {
            setCategoryName("");
        }
    };

    const handleCategoryNameChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setCategoryNameError(validations("categoryName", e.target.value));
        setCategoryName(e.target.value);
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        const nameError = validations("categoryName", categoryName);
        setCategoryNameError(nameError);
        if (!nameError) {
            addCategory({
                name: categoryName,
                user_id: id ?? "",
            });
        }
    };

    const handleNext = () => {
        dispatch(
            updateNewJobData({
                ...newJobData,
                job_category: agency,
            })
        );
        if (editJobId) {
            navigateTo(
                routeConstants.jobs.editAJob.root +
                    `${editJobId}/` +
                    routeConstants.jobs.editAJob.purpose
            );
        } else {
            navigateTo(
                routeConstants.jobs.postAJob.root +
                    routeConstants.jobs.postAJob.purpose
            );
        }
    };

    useEffect(() => {
        if (isError) {
            addToast({
                type: "error",
                text: "Something went wrong, please try again.",
            });
        }
    }, [isError]);

    useEffect(() => {
        if (newJobData?.job_category) {
            setAgency(newJobData?.job_category);
        }
    }, [newJobData?.job_category]);

    useEffect(() => {
        if (isSuccess && jobCategoryData) {
            toggleModal();
            refetch();
            setCategoryName("");
            addToast({
                type: "success",
                text: "Category added successfully!",
            });

            dispatch(
                updateNewJobData({
                    job_category: jobCategoryData.id,
                })
            );
        }
    }, [isSuccess, refetch, dispatch, jobCategoryData]);

    return {
        isModalOpen,
        toggleModal,
        agency,
        handleNext,
        categoryNameError,
        handleAgencyChange,
        handleSubmit,
        handleCategoryNameChange,
        data,
        categoryName,
        isFetching,
        isSubmittingForm,
        isEdit: newJobData?.isEdit,
    };
};
